import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';

/* =========================================================
   BASE URL
========================================================= */
const API_URL = process.env.REACT_APP_API_URL;

/* =========================================================
   STORAGE KEYS (must match auth.service.ts)
========================================================= */
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const ADMIN_DATA_KEY = 'admin_data';

/* =========================================================
   AXIOS INSTANCE
========================================================= */
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 30000,
});

/* =========================================================
   DEDICATED REFRESH CLIENT
   A separate axios instance so the refresh call itself is
   never intercepted (which would cause an infinite loop on
   401).
========================================================= */
const refreshClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 30000,
});

/* =========================================================
   REQUEST INTERCEPTOR — attach access token
========================================================= */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      config.headers = config.headers || ({} as any);
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   REFRESH STATE
========================================================= */
let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

const flushQueue = (err: unknown, token: string | null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (err) reject(err);
    else resolve(token as string);
  });
  pendingQueue = [];
};

const clearAuthAndRedirect = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ADMIN_DATA_KEY);
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

/* =========================================================
   RESPONSE INTERCEPTOR — auto-refresh on 401
========================================================= */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (AxiosRequestConfig & { _retry?: boolean })
      | undefined;

    const status = error.response?.status;
    const url = originalRequest?.url || '';

    // Don't try to refresh the refresh/login endpoints themselves.
    const isAuthEndpoint =
      url.includes('/auth/refresh-token') ||
      url.includes('/auth/login');

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (!refreshToken) {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      // If a refresh is already in-flight, queue this request.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({
            resolve: (newToken: string) => {
              originalRequest.headers = originalRequest.headers || {};
              (originalRequest.headers as any).Authorization =
                `Bearer ${newToken}`;
              resolve(apiClient(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // ⭐ FIX: refresh at /admin/auth/refresh-token.
        //
        // refreshClient.baseURL === REACT_APP_API_URL, which already
        // includes the /api prefix (e.g. http://localhost:5000/api).
        // The backend mounts the refresh endpoint at:
        //   app.use('/api/admin', adminRoutes)
        //     → routes/admin/index.ts: router.use('/auth', authRoutes)
        //       → auth.routes.ts: router.post('/refresh-token', ...)
        // so the correct request path (relative to baseURL) is
        // "/admin/auth/refresh-token". The previous value
        // "/api/auth/refresh-token" produced a double "/api/api/..."
        // URL which 404'd, causing a forced logout on every access-
        // token expiry.
        const { data } = await refreshClient.post(
          '/admin/auth/refresh-token',
          { refreshToken }
        );

        const newAccess: string | undefined = data?.data?.accessToken;
        const newRefresh: string | undefined = data?.data?.refreshToken;

        if (!newAccess || !newRefresh) {
          throw new Error('Refresh response missing tokens');
        }

        localStorage.setItem(ACCESS_TOKEN_KEY, newAccess);
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefresh);

        flushQueue(null, newAccess);

        originalRequest.headers = originalRequest.headers || {};
        (originalRequest.headers as any).Authorization =
          `Bearer ${newAccess}`;

        return apiClient(originalRequest);
      } catch (refreshErr) {
        flushQueue(refreshErr, null);
        clearAuthAndRedirect();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;